import { useState, useEffect } from "react"
import { useEditMode } from "@/contexts/EditModeContext"
import {Pencil} from 'lucide-react'

interface InlineEditProps {
  contentKey: string
  defaultValue: string
  as?: "h1" | "h2" | "h3" | "h4" | "p" | "span" | "div" | "label"
  className?: string
  multiline?: boolean
}

export default function InlineEdit({
  contentKey,
  defaultValue,
  as: Tag = "span",
  className = "",
  multiline = false,
}: InlineEditProps) {
  const { isEditMode, addContentEdit, contentEdits } = useEditMode()
  const [localValue, setLocalValue] = useState(defaultValue)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    const pending = contentEdits[contentKey]
    if (pending !== undefined) {
      setLocalValue(pending)
    } else {
      setLocalValue(defaultValue)
    }
  }, [defaultValue, contentEdits, contentKey])

  if (!isEditMode) {
    const saved = contentEdits[contentKey]
    return <Tag className={className}>{saved !== undefined ? saved : defaultValue}</Tag>
  }

  const handleChange = (newValue: string) => {
    setLocalValue(newValue)
    addContentEdit(contentKey, newValue)
  }

  const isModified = contentEdits[contentKey] !== undefined && contentEdits[contentKey] !== defaultValue

  if (multiline) {
    return (
      <div className="relative group">
        <textarea
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={`${className} w-full bg-transparent resize-none outline-none transition-all duration-200 ${
            isFocused
              ? "ring-2 ring-cyan-500/50 rounded-lg p-2 bg-slate-900/50"
              : isModified
              ? "ring-1 ring-amber-500/40 rounded-lg p-2 bg-amber-500/5"
              : "ring-1 ring-dashed ring-cyan-500/20 rounded-lg p-2 hover:ring-cyan-500/40 cursor-text"
          }`}
          rows={Math.max(2, localValue.split("\n").length)}
        />
        {!isFocused && !isModified && (
          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Pencil className="w-3.5 h-3.5 text-cyan-400" />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative group inline-block w-full">
      <input
        type="text"
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`${className} w-full bg-transparent outline-none transition-all duration-200 ${
          isFocused
            ? "ring-2 ring-cyan-500/50 rounded-lg px-2 py-1 bg-slate-900/50"
            : isModified
            ? "ring-1 ring-amber-500/40 rounded-lg px-2 py-1 bg-amber-500/5"
            : "ring-1 ring-dashed ring-cyan-500/20 rounded-lg px-2 py-1 hover:ring-cyan-500/40 cursor-text"
        }`}
      />
      {!isFocused && !isModified && (
        <div className="absolute top-1/2 -translate-y-1/2 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Pencil className="w-3.5 h-3.5 text-cyan-400" />
        </div>
      )}
    </div>
  )
}
